import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Modal, Form, Button } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import { 
  obtenerAdministradores, 
  update,
  crearAdministrador,
  habilitarAdministrador,
  deshabilitarAdministrador,
  reestablecerPassword
} from "../../api/usuarios.api.ts";
import Swal from 'sweetalert2';

export default function AdminListPage() {
  const { t, currentLanguage } = useContext(TranslatorContext);
  const [adminList, setAdminList] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [adminsPerPage] = useState(10);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    email: "",
    rut: "",
    dv: "",
    roles: []
  });
  const [createFormData, setCreateFormData] = useState({
    nombre: "",
    apellido: "",
    email: "",
    rut: "",
    dv: "",
    password: "",
    confirmPassword: "",
    roles: []
  });
  const [availableRoles] = useState([
    { id: 1, nombre: "Administrador" },
    { id: 3, nombre: "Master" },
    { id: 4, nombre: "Coordinador" }
  ]);

  // Cargar administradores
  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await obtenerAdministradores();
      if (!response.error) {
        setAdminList(response.usuarios);
        setFilteredAdmins(response.usuarios);
      } else {
        Swal.fire("Error", "No se pudo obtener el listado de administradores", "error");
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
      Swal.fire("Error", "Error al obtener administradores", "error");
    }
  };

  // Filtrar administradores
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredAdmins(adminList);
    } else {
      const filtered = adminList.filter(admin => {
        const searchLower = searchTerm.toLowerCase();
        return (
          admin.nombre?.toLowerCase().includes(searchLower) ||
          admin.apellido?.toLowerCase().includes(searchLower) ||
          admin.email?.toLowerCase().includes(searchLower) ||
          admin.rut?.toString().includes(searchTerm) ||
          `${admin.rut}-${admin.dv}`.includes(searchTerm)
        );
      });
      setFilteredAdmins(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, adminList]);

  // Paginación
  const totalAdmins = filteredAdmins.length;
  const totalPages = Math.ceil(totalAdmins / adminsPerPage);
  const indexOfLastAdmin = currentPage * adminsPerPage;
  const indexOfFirstAdmin = indexOfLastAdmin - adminsPerPage;
  const currentAdmins = filteredAdmins.slice(indexOfFirstAdmin, indexOfLastAdmin);

  const handlePageClick = (page) => {
    if (typeof page === "number" && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Abrir modal para editar
  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      nombre: admin.nombre,
      apellido: admin.apellido,
      email: admin.email,
      rut: admin.rut,
      dv: admin.dv,
      roles: admin.roles?.map(r => r.id) || []
    });
    setShowEditModal(true);
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingAdmin(null);
    setFormData({ nombre: "", apellido: "", email: "", rut: "", dv: "", roles: [] });
  };

  // Cerrar modal de creación
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateFormData({
      nombre: "",
      apellido: "",
      email: "",
      rut: "",
      dv: "",
      password: "",
      confirmPassword: "",
      roles: []
    });
  };

  // Guardar cambios (edición)
  const handleSaveChanges = async () => {
    if (!formData.nombre.trim() || !formData.apellido.trim() || !formData.email.trim()) {
      Swal.fire("Validación", "Todos los campos son requeridos", "warning");
      return;
    }

    if (formData.roles.length === 0) {
      Swal.fire("Validación", "Debe seleccionar al menos un rol", "warning");
      return;
    }

    try {
      const response = await update(
        editingAdmin.id,
        formData.email,
        formData.nombre,
        formData.apellido,
        formData.roles,
        [],
        []
      );

      if (!response.error) {
        Swal.fire("Éxito", "Administrador actualizado correctamente", "success");
        handleCloseEditModal();
        fetchAdmins();
      } else {
        Swal.fire("Error", response.msg || "Error al actualizar", "error");
      }
    } catch (error) {
      console.error("Error updating admin:", error);
      Swal.fire("Error", "Error al actualizar administrador", "error");
    }
  };

  // Crear nuevo administrador
  const handleCreateAdmin = async () => {
    // Validaciones
    if (!createFormData.nombre.trim() || !createFormData.apellido.trim() || 
        !createFormData.email.trim() || !createFormData.rut || !createFormData.dv ||
        !createFormData.password) {
      Swal.fire("Validación", "Todos los campos son requeridos", "warning");
      return;
    }

    if (createFormData.password !== createFormData.confirmPassword) {
      Swal.fire("Validación", "Las contraseñas no coinciden", "warning");
      return;
    }

    if (createFormData.roles.length === 0) {
      Swal.fire("Validación", "Debe seleccionar al menos un rol", "warning");
      return;
    }

    try {
      const response = await crearAdministrador(
        createFormData.email,
        parseInt(createFormData.rut),
        createFormData.dv,
        createFormData.nombre,
        createFormData.apellido,
        createFormData.password,
        createFormData.roles
      );

      if (!response.error) {
        Swal.fire("Éxito", "Administrador creado correctamente", "success");
        handleCloseCreateModal();
        fetchAdmins();
      } else {
        Swal.fire("Error", response.msg || "Error al crear administrador", "error");
      }
    } catch (error) {
      console.error("Error creating admin:", error);
      Swal.fire("Error", "Error al crear administrador", "error");
    }
  };

  // Habilitar usuario
  const handleEnable = async (admin) => {
    const result = await Swal.fire({
      title: "¿Habilitar?",
      text: `¿Habilitar a ${admin.nombre} ${admin.apellido}?`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Sí, habilitar",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        const response = await habilitarAdministrador(admin.id);
        if (!response.error) {
          Swal.fire("Éxito", "Administrador habilitado", "success");
          fetchAdmins();
        } else {
          Swal.fire("Error", response.msg || "Error al habilitar", "error");
        }
      } catch (error) {
        console.error("Error enabling admin:", error);
        Swal.fire("Error", "Error al habilitar administrador", "error");
      }
    }
  };

  // Deshabilitar usuario
  const handleDisable = async (admin) => {
    const result = await Swal.fire({
      title: "¿Deshabilitar?",
      text: `¿Deshabilitar a ${admin.nombre} ${admin.apellido}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, deshabilitar",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        const response = await deshabilitarAdministrador(admin.id);
        if (!response.error) {
          Swal.fire("Éxito", "Administrador deshabilitado", "success");
          fetchAdmins();
        } else {
          Swal.fire("Error", response.msg || "Error al deshabilitar", "error");
        }
      } catch (error) {
        console.error("Error disabling admin:", error);
        Swal.fire("Error", "Error al deshabilitar administrador", "error");
      }
    }
  };

  // Reestablecer password
  const handleResetPassword = async (admin) => {
    const result = await Swal.fire({
      title: "¿Reestablecer Contraseña?",
      text: `¿Reestablecer la contraseña a ${admin.nombre} ${admin.apellido}? Se enviará "Ctv2026." al email.`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Sí, reestablecer",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        const response = await reestablecerPassword(admin.id);
        if (!response.error) {
          Swal.fire("Éxito", "Contraseña reestablecida y enviada por email", "success");
        } else {
          Swal.fire("Error", response.msg || "Error al reestablecer contraseña", "error");
        }
      } catch (error) {
        console.error("Error resetting password:", error);
        Swal.fire("Error", "Error al reestablecer contraseña", "error");
      }
    }
  };

  const handleRoleToggle = (roleId) => {
    setCreateFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter(r => r !== roleId)
        : [...prev.roles, roleId]
    }));
  };

  // Toggle roles en modal de edición
  const handleEditRoleToggle = (roleId) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter(r => r !== roleId)
        : [...prev.roles, roleId]
    }));
  };

  // Función helper para convertir el campo activo a booleano
  const isAdminActive = (admin) => {
    console.log("Checking active status for admin:", admin.activo);
    if (admin.activo === true || admin.activo === 1) {
      return true;
    }
    if (admin.activo && typeof admin.activo === 'object' && admin.activo.data) {
      return admin.activo.data[0] === 1;
    }
    return Boolean(admin.activo);
  };

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className='mc-breadcrumb'>
              <h3 className="mc-breadcrumb-title">Administradores</h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                <li className="mc-breadcrumb-item">Gestión</li>
                <li className="mc-breadcrumb-item">Administradores</li>
              </ul>
            </div>
          </div>
        </Col>

        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 className="mc-card-title">Listado de Administradores, Masters y Coordinadores</h4>
              <Button
                variant="success"
                size="sm"
                onClick={() => setShowCreateModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <i className="material-icons" style={{ fontSize: '18px' }}>add</i>
                Nuevo Administrador
              </Button>
            </div>

            {/* Campo de búsqueda */}
            <div className="mc-card-search" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px' }}>
              <div style={{ position: 'relative', maxWidth: '300px' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar administrador..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingRight: searchTerm ? '40px' : '10px' }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      color: '#666'
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Tabla de administradores */}
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Nombre Completo</th>
                    <th>Email</th>
                    <th>RUT</th>
                    <th>Roles</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentAdmins.length > 0 ? (
                    currentAdmins.map((admin) => (
                      <tr key={admin.id}>
                        <td>{admin.nombre} {admin.apellido}</td>
                        <td>{admin.email}</td>
                        <td>{admin.rut}-{admin.dv}</td>
                        <td>
                          {admin.roles && admin.roles.map((role) => (
                            <span key={role.id} className="badge bg-info me-1">
                              {role.nombre}
                            </span>
                          ))}
                        </td>
                        <td>
                          {isAdminActive(admin) ? (
                            <span className="badge bg-success">Habilitado</span>
                          ) : (
                            <span className="badge bg-danger">Deshabilitado</span>
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm" role="group">
                            <button
                              type="button"
                              className="btn btn-primary"
                              title="Editar"
                              onClick={() => handleEdit(admin)}
                            >
                              <i className="material-icons">edit</i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-info"
                              title="Reestablecer Contraseña"
                              onClick={() => handleResetPassword(admin)}
                            >
                              <i className="material-icons">vpn_key</i>
                            </button>
                            {isAdminActive(admin) ? (
                              <button
                                type="button"
                                className="btn btn-warning"
                                title="Deshabilitar"
                                onClick={() => handleDisable(admin)}
                              >
                                <i className="material-icons">lock</i>
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-success"
                                title="Habilitar"
                                onClick={() => handleEnable(admin)}
                              >
                                <i className="material-icons">lock_open</i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No hay administradores registrados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="mc-paginate">
                <p className="mc-paginate-title">
                  mostrando <b>{currentAdmins.length}</b> de <b>{totalAdmins}</b> resultados
                </p>
                <ul className="mc-paginate-list">
                  <li
                    className="mc-paginate-item"
                    onClick={() => handlePageClick(currentPage - 1)}
                    style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                  >
                    {currentLanguage?.dir === "ltr" ? (
                      <i className="material-icons">chevron_left</i>
                    ) : (
                      <i className="material-icons">chevron_right</i>
                    )}
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                    <li
                      key={num}
                      className={`mc-paginate-item ${currentPage === num ? 'active' : ''}`}
                      onClick={() => handlePageClick(num)}
                      style={{ cursor: 'pointer' }}
                    >
                      {num}
                    </li>
                  ))}
                  <li
                    className="mc-paginate-item"
                    onClick={() => handlePageClick(currentPage + 1)}
                    style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                  >
                    {currentLanguage?.dir === "ltr" ? (
                      <i className="material-icons">chevron_right</i>
                    ) : (
                      <i className="material-icons">chevron_left</i>
                    )}
                  </li>
                </ul>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Modal de edición */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar Administrador</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Apellido</Form.Label>
              <Form.Control
                type="text"
                value={formData.apellido}
                onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>RUT</Form.Label>
              <Form.Control
                type="text"
                value={formData.rut}
                disabled
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>DV</Form.Label>
              <Form.Control
                type="text"
                value={formData.dv}
                disabled
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Roles</Form.Label>
              {availableRoles.map((role) => (
                <Form.Check
                  key={role.id}
                  type="checkbox"
                  id={`edit-role-${role.id}`}
                  label={role.nombre}
                  checked={formData.roles.includes(role.id)}
                  onChange={() => handleEditRoleToggle(role.id)}
                />
              ))}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveChanges}>
            Guardar Cambios
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de creación */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Crear Nuevo Administrador</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                value={createFormData.nombre}
                onChange={(e) => setCreateFormData({ ...createFormData, nombre: e.target.value })}
                placeholder="Ej: Juan"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Apellido</Form.Label>
              <Form.Control
                type="text"
                value={createFormData.apellido}
                onChange={(e) => setCreateFormData({ ...createFormData, apellido: e.target.value })}
                placeholder="Ej: Pérez"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={createFormData.email}
                onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                placeholder="Ej: juan@example.com"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>RUT (sin puntos ni guión)</Form.Label>
              <Form.Control
                type="text"
                value={createFormData.rut}
                onChange={(e) => setCreateFormData({ ...createFormData, rut: e.target.value })}
                placeholder="Ej: 12345678"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Dígito Verificador</Form.Label>
              <Form.Control
                type="text"
                value={createFormData.dv}
                onChange={(e) => setCreateFormData({ ...createFormData, dv: e.target.value.toUpperCase() })}
                placeholder="Ej: K"
                maxLength="1"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Contraseña</Form.Label>
              <Form.Control
                type="password"
                value={createFormData.password}
                onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                placeholder="Ingrese contraseña"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirmar Contraseña</Form.Label>
              <Form.Control
                type="password"
                value={createFormData.confirmPassword}
                onChange={(e) => setCreateFormData({ ...createFormData, confirmPassword: e.target.value })}
                placeholder="Confirme contraseña"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Roles (seleccione al menos uno)</Form.Label>
              {availableRoles.map((role) => (
                <Form.Check
                  key={role.id}
                  type="checkbox"
                  id={`role-${role.id}`}
                  label={role.nombre}
                  checked={createFormData.roles.includes(role.id)}
                  onChange={() => handleRoleToggle(role.id)}
                />
              ))}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleCreateAdmin}>
            Crear Administrador
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}
